import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Control, Controller, UseFormSetValue, useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { postsApi, programsApi } from 'api';
import ValidateTextField from 'components/_common/forms/TextField';
import { DraftEditor } from 'components/_common/DraftEditor/DraftEditor';
import { AttachmentList } from 'components/_new/Attachment';
import { Box, BoxSection } from 'components/_new/Box';
import ValidateDropdown from 'components/_common/forms/ValidateDropdown';
import CustomLabel from 'components/_common/forms/Label';
import { MultiSelect } from 'components/_new/MultiSelect';
import ValidateCheckbox from 'components/_common/forms/Checkbox';
import { Button } from 'components/_new/Button';
import { Format, PostAttachment, PostAttachmentLocal, UserData } from 'types/Dashboard';
import { useProgramPanel } from 'contexts/programPanelContext';
import { Application } from 'types/Application';
import { Avatar } from 'components/_new/Avatar';
import { userDisplayName } from 'utils/userDisplayName';
import { User } from 'types/User';
import { Dialog, DialogAction } from 'components/_new/Dialog';
import NotificationPost from 'components/wall/NotificationPost';
import { InputTextArea } from 'components/_new/InputTextArea/InputTextArea';

export type ProgramPostFormValues = {
	subject: string;
	content: string;
	recipients: string;
	applications: number[];
	withEmail: boolean;
	pinned: boolean;
};

type ProgramPostFormProps = {
	formId: string;
	onSubmit: React.FormEventHandler<HTMLFormElement>;
	control: Control<ProgramPostFormValues>;
	setValue: UseFormSetValue<ProgramPostFormValues>;
	recipients: string;
	defaultEditorContent?: string;
	attachments?: PostAttachment[];
	newAttachments: PostAttachment[];
	uploadedAttachments: PostAttachmentLocal[];
	onAttachmentUpload: (file: File) => void;
	onAttachmentRemove: (attachment: PostAttachment) => Promise<boolean | void>;
	isAddingAttachment: boolean;
	showWithEmailCheckbox: boolean;
	showSaveAsDraft: boolean;
	hasSubject: boolean;
	hasContent: boolean;
	onSaveAsDraft: () => void;
	onConfirmSubmit: () => void;
	previewLoading: boolean;
	saveDraftLoading: boolean;
	submitLoading: boolean;
	submitLabel: string;
	previewUser?: UserData;
	previewAttachments: PostAttachment[];
	previewExtra?: React.ReactNode;
	loadMentorsWhenRecipientsNotSelected?: boolean;
};

export const ProgramPostForm = ({
	formId,
	onSubmit,
	control,
	setValue,
	recipients,
	defaultEditorContent,
	attachments,
	newAttachments,
	uploadedAttachments,
	onAttachmentUpload,
	onAttachmentRemove,
	isAddingAttachment,
	showWithEmailCheckbox,
	showSaveAsDraft,
	hasSubject,
	hasContent,
	onSaveAsDraft,
	onConfirmSubmit,
	previewLoading,
	saveDraftLoading,
	submitLoading,
	submitLabel,
	previewUser,
	previewAttachments,
	previewExtra,
	loadMentorsWhenRecipientsNotSelected = false,
}: ProgramPostFormProps) => {
	const { t } = useTranslation();
	const { data: program, programName, isSuccess } = useProgramPanel();
	const [previewModalOpen, setPreviewModalOpen] = useState(false);
	const [pasteEmailsModalOpen, setPasteEmailsModalOpen] = useState(false);
	const [pasteEmailsText, setPasteEmailsText] = useState('');
	const isDisabled = !hasSubject || !hasContent;

	type ApplicationOption = {
		user: User;
		label: string;
		value: number;
	};

	const { data: applicationMenteesData, isLoading: applicationMenteesLoading } = useQuery(
		['getProgramApplications', { programName, applicationRole: 'mentee', acceptedOnly: true }],
		() =>
			programsApi.getProgramAdminApplicationsAll({
				programId: Number(program?.id),
				applicationRole: 'mentee',
			}),
		{
			enabled: isSuccess && recipients === 'selected',
		}
	);

	const { data: applicationMentorsData, isLoading: applicationMentorsLoading } = useQuery(
		['getProgramApplications', { programName, applicationRole: 'mentor', acceptedOnly: true }],
		() =>
			programsApi.getProgramAdminApplicationsAll({
				programId: Number(program?.id),
				applicationRole: 'mentor',
			}),
		{
			enabled: isSuccess && (recipients === 'selected' || loadMentorsWhenRecipientsNotSelected),
		}
	);

	const applicationDataLoading = applicationMenteesLoading || applicationMentorsLoading;
	const applicationsMerged = [...(applicationMenteesData || []), ...(applicationMentorsData || [])].filter(
		(application) => application.approved === 'accepted'
	);

	const mapApplicationOption = (application: Application): ApplicationOption => {
		const {
			id,
			programMembership: { user },
		} = application;
		return {
			user,
			label: userDisplayName(user),
			value: id,
		};
	};

	const applicationOptionTemplate = (option: ApplicationOption) => (
		<div className="inline-flex flex-row gap-2 align-items-center mr-2">
			<Avatar src={option.user.avatar} size="sm" name={userDisplayName(option.user, null)} />
			<span>{option.label}</span>
		</div>
	);
	const applicationSelectedOptionTemplate = (value: number) => {
		const application = applicationsMerged.find(({ id }) => id === value);
		if (!application) {
			return null;
		}
		return applicationOptionTemplate(mapApplicationOption(application));
	};
	const applicationOptions = [
		{
			label: t('misc.mentees'),
			items: (applicationMenteesData || [])
				.filter((application) => application.approved === 'accepted')
				.sort((a, b) =>
					String(a.programMembership.user.lastName || '').localeCompare(
						String(b.programMembership.user.lastName || ''),
						'en',
						{ sensitivity: 'base' }
					)
				)
				.map(mapApplicationOption),
		},
		{
			label: t('misc.mentors'),
			items: (applicationMentorsData || [])
				.filter((application) => application.approved === 'accepted')
				.sort((a, b) =>
					String(a.programMembership.user.lastName || '').localeCompare(
						String(b.programMembership.user.lastName || ''),
						'en',
						{ sensitivity: 'base' }
					)
				)
				.map(mapApplicationOption),
		},
	];

	const watchedApplications = useWatch({ control, name: 'applications' }) || [];
	const watchedSubject = useWatch({ control, name: 'subject' });
	const watchedContent = useWatch({ control, name: 'content' });
	const watchedPinned = useWatch({ control, name: 'pinned' });
	const selectedApplications =
		recipients === 'selected'
			? (watchedApplications
					.map((applicationId) => applicationsMerged.find((application) => application.id === applicationId))
					.filter(Boolean) as Application[])
			: [];

	const extractEmails = (text: string): string[] => {
		const emailRegex = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
		const foundEmails = text.match(emailRegex) || [];
		return [...new Set(foundEmails.map((email) => email.trim().toLowerCase()))];
	};

	const getApplicationEmail = (application: Application): string =>
		String(application?.programMembership?.user?.email || application?.systemAnswers?.email || '')
			.trim()
			.toLowerCase();

	const parsedEmails = useMemo(() => extractEmails(pasteEmailsText), [pasteEmailsText]);
	const parsedEmailsSet = useMemo(() => new Set(parsedEmails), [parsedEmails]);

	const matchedApplications = useMemo(
		() =>
			applicationsMerged.filter((application) => {
				const appEmail = getApplicationEmail(application);
				return appEmail.length > 0 && parsedEmailsSet.has(appEmail);
			}),
		[applicationsMerged, parsedEmailsSet]
	);

	const matchedEmailsSet = useMemo(
		() => new Set(matchedApplications.map((application) => getApplicationEmail(application)).filter(Boolean)),
		[matchedApplications]
	);

	const unmatchedEmails = useMemo(
		() => parsedEmails.filter((email) => !matchedEmailsSet.has(email)),
		[parsedEmails, matchedEmailsSet]
	);

	const handlePasteEmailsApply = () => {
		setValue(
			'applications',
			matchedApplications.map((application) => application.id),
			{ shouldDirty: true, shouldValidate: true }
		);
		setPasteEmailsModalOpen(false);
	};

	return (
		<>
			<Dialog
				title={t('programPanel.post.postPreview')}
				visible={previewModalOpen}
				onHide={() => setPreviewModalOpen(false)}
				size="lg"
			>
				{program && previewUser && (
					<>
						<NotificationPost
							postData={{
								id: 0,
								language: 'pl',
								format: Format.HTML,
								menteeAccess: ['mentees', 'all'].includes(recipients),
								mentorAccess: ['mentors', 'all'].includes(recipients),
								postedBy: 'user',
								programId: program?.id,
								text: watchedContent || '',
								title: watchedSubject || '',
								user: previewUser,
								userId: previewUser?.id,
								updatedAt: new Date(),
								programType: 'test',
								applicationRecievers: selectedApplications,
								attachments: previewAttachments,
								pinnedAt: watchedPinned ? new Date() : null,
							}}
							adminMode
							previewMode
						/>
						{previewExtra}
					</>
				)}
			</Dialog>
			<DialogAction
				title={t('programPanel.post.pasteEmailListTitle')}
				visible={pasteEmailsModalOpen}
				onHide={() => setPasteEmailsModalOpen(false)}
				size="lg"
				actions={[
					{
						key: 'add',
						label: t('actions.add'),
						onClick: handlePasteEmailsApply,
					},
				]}
			>
				<div className="flex flex-column md:flex-row gap-4">
					<div className="w-full md:w-6">
						<div className="font-semibold mb-2">{t('programPanel.post.pasteEmailListInputTitle')}</div>
						<InputTextArea
							value={pasteEmailsText}
							onChange={(event) => setPasteEmailsText(event.target.value)}
							rows={14}
							placeholder={t('programPanel.post.pasteEmailListInputPlaceholder')}
							className="w-full"
						/>
						<div className="text-sm mt-2">
							{t('programPanel.post.pasteEmailListDetectedCount')}: {parsedEmails.length}
						</div>
					</div>
					<div className="w-full md:w-6">
						<div className="font-semibold mb-2">{t('programPanel.post.pasteEmailListMatchedTitle')}</div>
						<div className="text-sm mb-4">
							{matchedApplications.length > 0
								? matchedApplications.map((application) => {
										const option = mapApplicationOption(application);
										const email = getApplicationEmail(application);
										return (
											<div
												key={application.id}
												className="flex flex-row gap-3 align-items-start border-1 surface-border border-round p-2 mb-2"
											>
												<Avatar
													src={option.user.avatar}
													size="sm"
													name={userDisplayName(option.user, null)}
												/>
												<div className="flex flex-column min-w-0">
													<span className="font-semibold">{option.label}</span>
													<span className="text-sm text-color-secondary text-wrap-any">
														{email}
													</span>
												</div>
											</div>
										);
								  })
								: t('programPanel.post.pasteEmailListNoMatches')}
						</div>

						<div className="font-semibold mb-2">{t('programPanel.post.pasteEmailListUnmatchedTitle')}</div>
						<div className="text-sm">
							{unmatchedEmails.length > 0
								? unmatchedEmails.join(', ')
								: t('programPanel.post.pasteEmailListNoUnmatched')}
						</div>
					</div>
				</div>
			</DialogAction>
			<form id={formId} onSubmit={onSubmit}>
				<Box variant="white-bordered" className="gap-4">
					<BoxSection header={t('programPanel.post.contentSectionTitle')}>
						<ValidateTextField
							label={t('programPanel.post.subject')}
							name="subject"
							control={control}
							required
						/>
						<DraftEditor
							defaultValue={defaultEditorContent}
							onChange={(value) => {
								setValue('content', String(value));
							}}
							uploadRequestFunction={postsApi.uploadImage}
						/>
						<AttachmentList
							attachments={attachments}
							newAttachments={newAttachments}
							uploadedAttachments={uploadedAttachments}
							onUpload={onAttachmentUpload}
							onRemove={onAttachmentRemove}
							isAdding={isAddingAttachment}
							options={{
								addAttachmentLabel: t('programPanel.post.addAttachment'),
								removeAttachmentLabel: t('programPanel.post.addAttachment'),
								uploadDesc: t('programPanel.post.attachmentUploadDescription', { count: 8 }),
							}}
						/>
					</BoxSection>
					<BoxSection header={t('programPanel.post.settingsSectionTitle')}>
						<ValidateDropdown
							name="recipients"
							label={t('programPanel.recipients')}
							control={control}
							options={[
								{
									label: t('programPanel.recipientOptions.all'),
									value: 'all',
								},
								{
									label: t('programPanel.recipientOptions.mentorsOnly'),
									value: 'mentors',
								},
								{
									label: t('programPanel.recipientOptions.menteesOnly'),
									value: 'mentees',
								},
								{
									label: t('programPanel.recipientOptions.selected'),
									value: 'selected',
								},
							]}
							required
						/>

						{recipients === 'selected' && (
							<div className="flex flex-column align-items-start gap-1">
								<Controller
									control={control}
									name="applications"
									rules={{ required: t('programPanel.selectedPersons.required') }}
									render={({ field, fieldState: { error } }) => (
										<div className="w-full">
											<CustomLabel
												name="applications"
												label={t('programPanel.selectedPersons.label')}
												required
											/>
											<MultiSelect
												{...field}
												options={applicationOptions}
												placeholder={t('programPanel.recipientOptions.selected')}
												className="w-full"
												itemTemplate={applicationOptionTemplate}
												selectedItemTemplate={applicationSelectedOptionTemplate}
												panelClassName="px-3"
												loading={applicationDataLoading}
												filter
												filterMatchMode="contains"
											/>
											{error && <small className="p-error font-bold ml-3">{error.message}</small>}
										</div>
									)}
								/>
								<Button
									label={t('programPanel.post.pasteEmailListButton')}
									variant="primary-text"
									variantSize="sm"
									icon="paste"
									iconSet="fa"
									iconStyle="solid"
									onClick={() => setPasteEmailsModalOpen(true)}
								/>
							</div>
						)}

						{showWithEmailCheckbox && (
							<ValidateCheckbox
								text={t('programPanel.post.sendEmail')}
								name="withEmail"
								control={control}
							/>
						)}
						<ValidateCheckbox text={t('programPanel.post.pinned')} name="pinned" control={control} />
					</BoxSection>
					<BoxSection horizontal contentClassName="justify-content-end">
						<Button
							onClick={() => setPreviewModalOpen(true)}
							label={t('actions.preview')}
							variant="primary-outlined"
							loading={previewLoading}
							disabled={isDisabled}
						/>
						{showSaveAsDraft && (
							<Button
								onClick={onSaveAsDraft}
								label={t('actions.saveAsDraft', 'Zapisz jako szkic')}
								variant="secondary-outlined"
								loading={saveDraftLoading}
								disabled={isDisabled}
							/>
						)}
						<Button
							onClick={onConfirmSubmit}
							label={submitLabel}
							variant="primary"
							disabled={isDisabled}
							loading={submitLoading}
						/>
					</BoxSection>
				</Box>
			</form>
		</>
	);
};
